import type { TravelAgencyTour } from '@eliadmin/core/src/api/travel-agency/travel-agency-api';

import { TourTabHeading } from '../tour-content';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { TourReservationForm } from '../../tour-reservation-form';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { formatTourPrice } from '../../../../utils/tour-price-utils';
import { formatTourDateRange as formatDateRange } from '../../../../utils/tour-date-utils';

const AVAILABLE_TOUR_STATUS_ID = 1;
const PRICE_INCLUDES_ATTRIBUTE_ID = 4;
const PRICE_EXCLUDES_ATTRIBUTE_ID = 5;

export function PricesTab({ tour }: { tour: TravelAgencyTour }) {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const currency = useGlobalStore(state => state.generalLayout.currency);

  const table_headings = [
    t('text3960'),
    t('text1851'),
    'Počet dní / nocí',
    'Stav',
    'Cena / os.',
    t('text1335'),
  ];

  const currentDate = new Date();

  const parseTourDate = (value?: string | null) => {
    if (!value) return null;

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const getDayText = (days: number) => {
    if (locale === 'cs') {
      if (days === 1) return 'den';
      if (days >= 2 && days <= 4) return 'dny';
      return 'dní';
    }
    if (days === 1) return 'deň';
    if (days >= 2 && days <= 4) return 'dni';
    return 'dní';
  };

  const getNightText = (nights: number) => {
    if (nights === 1) return 'noc';
    if (nights >= 2 && nights <= 4) return 'noci';
    return 'nocí';
  };

  const getFreeSlotText = (slots: number) => {
    if (locale === 'cs') {
      if (slots === 1) return 'volné místo';
      if (slots >= 2 && slots <= 4) return 'volná místa';
      return 'volných míst';
    }
    if (slots === 1) return 'voľné miesto';
    if (slots >= 2 && slots <= 4) return 'voľné miesta';
    return 'voľných miest';
  };

  const isPastDate = (startDate: string) => {
    const parsedStartDate = parseTourDate(startDate);

    return parsedStartDate ? parsedStartDate < currentDate : false;
  };

  const sortedDates = [...tour.dates].sort((a, b) => {
    const aTime = parseTourDate(a.date_start)?.getTime() ?? Number.MAX_SAFE_INTEGER;
    const bTime = parseTourDate(b.date_start)?.getTime() ?? Number.MAX_SAFE_INTEGER;

    return aTime - bTime;
  });

  const hasDiscount = (p: TravelAgencyTour['dates'][number]) =>
    typeof p.discount === 'number' &&
    p.discount > 0 &&
    typeof p.price_with_vat_before_discount === 'number' &&
    typeof p.price_with_vat === 'number' &&
    p.price_with_vat_before_discount > p.price_with_vat;

  const priceIncludes = tour.attributes.find(attr => attr.id === PRICE_INCLUDES_ATTRIBUTE_ID);
  const priceExcludes = tour.attributes.find(attr => attr.id === PRICE_EXCLUDES_ATTRIBUTE_ID);

  return (
    <div className="flex w-full flex-col items-start justify-start gap-8">
      <TourTabHeading title={t('text3957')} />

      {/* DESKTOP TABLE */}
      <table className="hidden w-full border-collapse md:table">
        <thead className="w-full">
          <tr className="border-b-2 border-b-[#E7E7E9]">
            {table_headings.map((heading, index) => (
              <th key={index} className="py-3 text-left font-bold text-foreground">
                {heading}
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="mt-4">
          {sortedDates.map((price, index) => (
            <tr key={index} className="w-full border-b border-b-[#E7E7E9]">
              <td className="h-[70px] text-sm text-foreground lg:text-base">{price.name}</td>

              <td className="h-[70px] text-sm text-foreground lg:text-base">
                {formatDateRange(price.date_start, price.date_stop)}
              </td>

              <td className="h-[70px] text-sm text-foreground lg:text-base">
                {price.days_count} {getDayText(price.days_count)} / {price.nights_count}{' '}
                {getNightText(price.nights_count)}
              </td>

              <td className="h-[70px] text-sm font-bold lg:text-base">
                <div className="flex h-full flex-col items-start justify-center gap-1">
                  <span
                    style={{
                      color: isPastDate(price.date_start)
                        ? '#ad1d1d'
                        : price.agency_tour_status?.color || '#000000',
                    }}
                  >
                    {isPastDate(price.date_start)
                      ? t('text495')
                      : price.agency_tour_status?.name || t('text3963')}
                  </span>
                  {price.show_free_slots && typeof price.free_slots === 'number' && (
                    <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-semibold text-destructive-foreground">
                      {price.free_slots} {getFreeSlotText(price.free_slots)}
                    </span>
                  )}
                </div>
              </td>

              <td className="flex h-[70px] flex-col items-start justify-center gap-0 text-sm font-bold text-[#008000] lg:text-base">
                {formatTourPrice(price.price_with_vat, locale)} {currency}
                {hasDiscount(price) && (
                  <span className="text-sm font-normal text-foreground line-through lg:text-base">
                    {formatTourPrice(price.price_with_vat_before_discount, locale)} {currency}
                  </span>
                )}
              </td>

              <td className="h-[70px] border-b border-b-[#E7E7E9] text-sm lg:text-base">
                {isPastDate(price.date_start) ? (
                  <Button
                    variant="destructive"
                    className="w-full cursor-context-menu bg-[#ad1d1dff] text-white hover:bg-[#ad1d1dff]"
                  >
                    {t('text495')}
                  </Button>
                ) : price.agency_tour_status?.id === AVAILABLE_TOUR_STATUS_ID ? (
                  <TourReservationForm tour={tour} tourId={tour.id} price={price} />
                ) : (
                  <Button
                    variant="destructive"
                    className="w-full cursor-context-menu bg-[#ad1d1dff] text-white hover:bg-[#ad1d1dff]"
                  >
                    {t('text3962')}
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* MOBILE LIST */}
      {sortedDates.map((price, index) => (
        <div key={index} className="flex w-full flex-col items-start justify-start gap-4 md:hidden">
          <div className="flex w-full items-center justify-between">
            <span className="text-sm font-bold sm:text-base">{t('text3960')}</span>
            <p className="text-right text-sm font-normal text-foreground sm:text-base">
              {price.name}
            </p>
          </div>

          <div className="flex w-full items-center justify-between">
            <span className="text-sm font-bold sm:text-base">{t('text3961')}</span>
            <p className="text-right text-sm font-normal text-foreground sm:text-base">
              {price.days_count} {getDayText(price.days_count)} / {price.nights_count}{' '}
              {getNightText(price.nights_count)}
            </p>
          </div>

          <div className="flex w-full items-center justify-between">
            <span className="text-sm font-bold sm:text-base">Cena / os.</span>
            <div className="flex items-center justify-end gap-3">
              {hasDiscount(price) && (
                <p className="text-xs font-normal text-foreground line-through sm:text-sm">
                  {formatTourPrice(price.price_with_vat_before_discount, locale)} {currency}
                </p>
              )}
              <p className="text-right text-sm font-bold text-[#008000] sm:text-base">
                {formatTourPrice(price.price_with_vat, locale)} {currency}
              </p>
            </div>
          </div>

          <div className="flex w-full items-center justify-between">
            <span className="text-sm font-bold sm:text-base">{t('text1851')}</span>
            <p className="text-right text-sm font-normal text-foreground sm:text-base">
              {formatDateRange(price.date_start, price.date_stop)}
            </p>
          </div>

          <div className="flex w-full items-center justify-between">
            <span className="text-sm font-bold sm:text-base">Stav</span>
            <div className="flex flex-col items-end gap-1">
              <p
                className="text-right text-sm font-bold sm:text-base"
                style={{
                  color: isPastDate(price.date_start) ? '#ad1d1d' : price.agency_tour_status?.color,
                }}
              >
                {isPastDate(price.date_start)
                  ? t('text495')
                  : price.agency_tour_status?.name || t('text3963')}
              </p>
              {price.show_free_slots && typeof price.free_slots === 'number' && (
                <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-semibold text-destructive-foreground">
                  {price.free_slots} {getFreeSlotText(price.free_slots)}
                </span>
              )}
            </div>
          </div>

          <div className="flex w-full items-center justify-end">
            {isPastDate(price.date_start) ? (
              <Button
                variant="destructive"
                className="cursor-context-menu bg-[#ad1d1dff] text-white hover:bg-[#ad1d1dff]"
              >
                {t('text495')}
              </Button>
            ) : price.agency_tour_status?.id === AVAILABLE_TOUR_STATUS_ID ? (
              <TourReservationForm tour={tour} tourId={tour.id} price={price} />
            ) : (
              <Button
                variant="destructive"
                className="cursor-context-menu bg-[#ad1d1dff] text-white hover:bg-[#ad1d1dff]"
              >
                {t('text3962')}
              </Button>
            )}
          </div>
        </div>
      ))}
      <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-2 lg:gap-8">
        <div className="col-span-1 flex h-fit w-full flex-col items-start justify-start gap-2 rounded-[24px] bg-[#F6F6F6] p-6 sm:gap-4 lg:p-8">
          <h3 data-ea-visual-tag="h4" className="text-sm font-bold text-foreground sm:text-base">{t('text3964')}</h3>
          <ul className="flex w-full flex-col items-start justify-start gap-2">
            {priceIncludes?.values &&
              priceIncludes.values.map(item => (
                <li
                  key={item.name}
                  className="flex items-center justify-start gap-2 text-sm font-light text-foreground"
                >
                  <Image
                    src={'/images/check-green.svg'}
                    alt={item.name}
                    width={16}
                    height={16}
                    className="min-w-4"
                  />
                  {item.name}
                </li>
              ))}
          </ul>
        </div>
        <div className="col-span-1 flex h-fit w-full flex-col items-start justify-start gap-2 rounded-[24px] bg-[#F6F6F6] p-6 sm:gap-4 lg:p-8">
          <h3 data-ea-visual-tag="h4" className="text-sm font-bold text-foreground sm:text-base">{t('text3965')}</h3>
          <ul className="flex w-full flex-col items-start justify-start gap-2">
            {priceExcludes?.values &&
              priceExcludes.values.map(item => (
                <li
                  key={item.name}
                  className="flex items-center justify-start gap-2 text-sm font-light text-foreground"
                >
                  <Image
                    src={'/images/close.svg'}
                    alt={item.name}
                    width={16}
                    height={16}
                    className="min-w-4"
                  />
                  {item.name}
                </li>
              ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
